import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, FileText, Sparkles, Trash2, Loader2 } from "lucide-react";
import { useApp } from "@/app/AppLayout";
import { Button } from "@/components/ui/button";
import api, { apiErr } from "@/lib/api";

export default function Documents() {
  const { setCredits, refreshMeta } = useApp();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [analysis, setAnalysis] = useState({});
  const fileRef = useRef();

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/documents", fd); toast.success("Document importé"); load(); }
    catch (e2) { toast.error(apiErr(e2)); } finally { setUploading(false); fileRef.current.value = ""; }
  };

  const analyze = async (id) => {
    setAnalyzing(id);
    try {
      const { data } = await api.post(`/documents/${id}/analyze`);
      setAnalysis((a) => ({ ...a, [id]: data.analysis })); setOpenId(id);
      setCredits(data.credits); refreshMeta(); toast.success(`Analyse terminée (${data.cost} crédits)`);
    } catch (e) { toast.error(apiErr(e)); } finally { setAnalyzing(null); }
  };

  const remove = async (id) => { await api.delete(`/documents/${id}`); load(); };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Documents</h1>
      <p className="text-muted2 mt-1">Importez vos PDF, DOCX ou TXT pour les résumer, analyser et comparer à votre projet.</p>

      <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" onChange={upload} className="hidden" data-testid="doc-file-input" />
      <button onClick={() => fileRef.current.click()} disabled={uploading} className="mt-6 w-full border-2 border-dashed border-[#E5E7EB] rounded-2xl p-8 hover:border-navy transition-all flex flex-col items-center gap-2" data-testid="doc-upload-btn">
        {uploading ? <Loader2 className="h-7 w-7 text-navy animate-spin" /> : <Upload className="h-7 w-7 text-navy" />}
        <span className="text-sm font-medium text-navy">{uploading ? "Importation…" : "Importer un document"}</span>
        <span className="text-xs text-muted2">PDF, DOCX, TXT</span>
      </button>

      <div className="mt-6 space-y-3">
        {docs.map((d) => (
          <div key={d.id} className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid={`doc-${d.id}`}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-navy/5 flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-navy" /></div>
              <div className="flex-1 min-w-0"><p className="font-medium text-navy truncate">{d.filename}</p><p className="text-xs text-muted2">{Math.round((d.char_count || 0) / 1000)}k caractères</p></div>
              <Button size="sm" onClick={() => analyze(d.id)} disabled={analyzing === d.id} className="bg-navy text-white hover:bg-navy-800 rounded-full gap-1" data-testid={`analyze-${d.id}`}>
                {analyzing === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Analyser
              </Button>
              <button onClick={() => remove(d.id)} className="text-muted2 hover:text-red-500 p-1"><Trash2 className="h-4 w-4" /></button>
            </div>
            {(openId === d.id || (d.analysis && openId === d.id)) && (analysis[d.id] || d.analysis) && (
              <div className="mt-4 pt-4 border-t border-[#E5E7EB] text-sm text-ink whitespace-pre-wrap leading-relaxed">{analysis[d.id] || d.analysis}</div>
            )}
            {d.analysis && openId !== d.id && <button onClick={() => setOpenId(d.id)} className="text-xs text-navy mt-2 hover:underline">Voir l'analyse</button>}
          </div>
        ))}
        {docs.length === 0 && <p className="text-center text-muted2 py-10">Aucun document importé.</p>}
      </div>
    </div>
  );
}
